import { onAuthStateChanged } from 'firebase/auth';
import { useEffect, useMemo, useState } from 'react';
import { auth, isFirebaseConfigured } from '../firebase/client';

const staffEmailSet = new Set(
  (process.env.REACT_APP_STAFF_EMAILS || process.env.REACT_APP_ADMIN_EMAILS || '')
    .split(',')
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean)
);

/**
 * React auth hook that exposes user, claims, and staff/admin access flags.
 * Staff is granted by custom claim `staff: true`, `admin: true`, or allowlisted email.
 * Admin is granted by custom claim `admin: true`.
 * @returns {{
 *   user: any,
 *   claims: Record<string, any>,
 *   isLoading: boolean,
 *   isAdmin: boolean,
 *   isStaff: boolean
 * }}
 */
export function useAuthUser() {
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isFirebaseConfigured || !auth) {
      setUser(null);
      setClaims({});
      setIsLoading(false);
      return undefined;
    }

    return onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser);
      if (!nextUser) {
        setClaims({});
        setIsLoading(false);
        return;
      }

      const idTokenResult = await nextUser.getIdTokenResult(true);
      setClaims(idTokenResult.claims || {});
      setIsLoading(false);
    });
  }, []);

  const isAdmin = useMemo(() => Boolean(user && claims?.admin === true), [claims, user]);

  const isStaff = useMemo(() => {
    if (!user) {
      return false;
    }
    if (isAdmin || claims?.staff === true) {
      return true;
    }
    const normalizedEmail = (user.email || '').toLowerCase();
    return staffEmailSet.has(normalizedEmail);
  }, [claims, isAdmin, user]);

  return {
    user,
    claims,
    isLoading,
    isAdmin,
    isStaff,
  };
}
